import React, { useCallback, useMemo, useState } from "react";

import EntityImageManager from "./EntityImageManager";
import type { GalleryImage } from "./entityImages.types";
import ImageLightbox, { type LightboxImage } from "./ImageLightbox";
import type { UseEntityImages } from "./useEntityImages";

type EntityImageGalleryProps = {
	gallery: UseEntityImages;
	editable: boolean;
	/** Used for the lightbox alt text / heading. */
	title: string;
};

/**
 * Convenience wrapper that pairs the gallery editor with its own lightbox. Used inside the entity
 * create/edit dialogs so each dialog only wires up a single component.
 */
const EntityImageGallery = ({ gallery, editable, title }: EntityImageGalleryProps) => {
	const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
	const closeLightbox = useCallback((): void => setLightboxIndex(null), []);

	const lightboxImages = useMemo<LightboxImage[]>(
		() => gallery.images.map((image: GalleryImage) => ({ url: image.url, name: image.name })),
		[gallery.images]
	);

	return (
		<>
			<EntityImageManager
				busy={gallery.busy}
				editable={editable}
				failed={gallery.failed}
				images={gallery.images}
				onAddFiles={gallery.addFiles}
				onOpen={setLightboxIndex}
				onRemove={gallery.removeImage}
				onSetDefault={gallery.setDefault}
			/>

			{lightboxIndex !== null && lightboxImages.length > 0 && (
				<ImageLightbox
					images={lightboxImages}
					index={Math.min(lightboxIndex, lightboxImages.length - 1)}
					onClose={closeLightbox}
					onIndexChange={setLightboxIndex}
					title={title}
				/>
			)}
		</>
	);
};

export default EntityImageGallery;
